import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import { alpha, Box, Divider, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { EMPTY_ARRAY, EMPTY_OBJECT, useTenderDetails } from '../../contexts/TenderDetailsContext';
import ProposalStructure from './ProposalStructure';
import StrategyAndGapFillerCard from './StrategyAndGapFillerCard';
import WorkflowStep from './WorkflowStep';

const StructureAndOutlineStep: React.FC = () => {
  const theme = useTheme();
  const {
    canManageTender,
    openStep,
    setOpenStep,
    stepCompletion,
    toggleStepCompletion,
    proposalStructure,
    setProposalStructure,
    removeHeading,
    isUploadingStructure,
    handleStructureFileUpload,
    downloadStructureTemplate,
    sensors,
    handleDragEnd,
    handleHeadingUpdate,
    handleFocuserUpdate,
    tender,
    handleLoadExtractedStructure,
    selectedTemplates,
    availableTemplates,
    handleSaveStructureAsDefault,
    setIsSavingDefault,
    account,
    quickSettings,
    handleQuickSettingsChange,
    handleDownload,
  } = useTenderDetails();

  if (!canManageTender) {
    return null;
  }

  return (
    <WorkflowStep
      stepNumber={7}
      expanded={openStep === 7}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 7 : null)}
      title="Strategy & Structure"
      description="Define global bid positioning, resolve capability gaps, and configure the document outline and section focusers."
      isComplete={!!stepCompletion[7]}
      onToggleComplete={() => toggleStepCompleteHandler()}
    >
      {/* Part 1: Strategy & Gap Mitigation */}
      {tender && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderRadius: 2,
            borderColor: alpha(theme.palette.primary.main, 0.2),
            bgcolor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
            <TrackChangesOutlinedIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem' }}>
              1. Bid Strategy &amp; Gap Mitigation
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set overarching strategic and commercial directives and resolve or dismiss capability
            gaps before generating sections.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <StrategyAndGapFillerCard tender={tender} mode="interactive" />
        </Paper>
      )}

      {/* Part 2: Document Structure & Section Focusers */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          borderColor: alpha(theme.palette.primary.main, 0.2),
          bgcolor: alpha(theme.palette.background.paper, 0.6),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
          <AccountTreeOutlinedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem' }}>
            2. Document Structure &amp; Section Focusers
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Organize your proposal headings and review selective section-level focus instructions.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ProposalStructure
          proposalStructure={proposalStructure}
          setProposalStructure={setProposalStructure}
          onRemoveHeading={removeHeading}
          isUploadingStructure={isUploadingStructure}
          onStructureFileUpload={handleStructureFileUpload}
          onDownloadStructureTemplate={downloadStructureTemplate}
          sensors={sensors}
          onDragEnd={handleDragEnd}
          onHeadingUpdate={handleHeadingUpdate}
          onFocuserUpdate={handleFocuserUpdate}
          canLoadExtractedStructure={
            !!(tender?.extractedStructure && tender.extractedStructure.length > 0)
          }
          canLoadHybridExtractedStructure={
            !!(tender?.hybridExtractedStructure && tender.hybridExtractedStructure.length > 0)
          }
          onLoadExtractedStructure={handleLoadExtractedStructure}
          selectedTemplate={selectedTemplates.draft || 'system_default'}
          mergeFields={(() => {
            if (!selectedTemplates.draft || selectedTemplates.draft === 'system_default') {
              return ['INSERT EXECUTIVE SUMMARY', 'INSERT DRAFT SECTIONS'];
            }
            if (tender?.draftTemplateMergeFields && tender.draftTemplateMergeFields.length > 0) {
              return tender.draftTemplateMergeFields;
            }
            const customTemplate = availableTemplates.find(
              // biome-ignore lint/suspicious/noExplicitAny: Expected
              (t: any) => t.id === selectedTemplates.draft
            );
            if (customTemplate?.validTagsFound && customTemplate.validTagsFound.length > 0) {
              return customTemplate.validTagsFound;
            }
            return [];
          })()}
          onSaveStructureAsDefault={() =>
            handleSaveStructureAsDefault(proposalStructure, setIsSavingDefault)
          }
          savedStructureTemplate={account?.customProposalStructure}
          detectedForms={tender?.detectedForms || EMPTY_ARRAY}
          detectedFormLinks={tender?.detectedFormLinks || EMPTY_OBJECT}
          enabledForms={quickSettings.enabledForms || EMPTY_ARRAY}
          onEnabledFormsChange={(forms) => handleQuickSettingsChange({ enabledForms: forms })}
          onDownloadForm={(url, filename) => handleDownload(url, 'detected_form', filename)}
        />
      </Paper>
    </WorkflowStep>
  );

  function toggleStepCompleteHandler() {
    toggleStepCompletion(7);
  }
};

export default StructureAndOutlineStep;
